import type { BookingUserNotificationDto } from 'noddi-types';
import { useState } from 'react';

export type BookingSmsOptIns = {
  userIds: ReadonlySet<number>;
  setOptIn: (userId: number, optedIn: boolean) => void;
  notificationsToOverride: BookingUserNotificationDto[];
};

type ScopedOptIns = { userGroupId: number | null; userIds: ReadonlySet<number> };

const NO_OPT_INS: ReadonlySet<number> = new Set();

/**
 * Members turned on for SMS for this booking only, on top of the backend
 * default (the car's managers): an organization booker opting in, or a driver
 * who has booking SMS off. Sent as `notificationsToOverride` on create.
 *
 * Opt-ins belong to one organization: switching to another group, or to a
 * non-organization booking (`null`), drops them so none leak into its booking.
 */
export const useBookingSmsOptIns = (organizationUserGroupId: number | null): BookingSmsOptIns => {
  const [optIns, setOptIns] = useState<ScopedOptIns>({
    userGroupId: organizationUserGroupId,
    userIds: NO_OPT_INS
  });
  // Reset during render (not in an effect) so no stale opt-in is ever rendered or sent.
  if (optIns.userGroupId !== organizationUserGroupId) {
    setOptIns({ userGroupId: organizationUserGroupId, userIds: NO_OPT_INS });
  }
  const userIds = optIns.userGroupId === organizationUserGroupId ? optIns.userIds : NO_OPT_INS;

  const setOptIn = (userId: number, optedIn: boolean) => {
    if (organizationUserGroupId == null) {
      return;
    }
    setOptIns((prev) => {
      const next = new Set(prev.userGroupId === organizationUserGroupId ? prev.userIds : NO_OPT_INS);
      if (optedIn) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return { userGroupId: organizationUserGroupId, userIds: next };
    });
  };

  const notificationsToOverride = [...userIds].map((userId) => ({ userId, smsBookingNotifications: true }));

  return { userIds, setOptIn, notificationsToOverride };
};
